import { InputContainer } from "@/components/ui/forms/inputContainer"
import { Input } from "@/components/ui/baseUi/input"
import { cn } from "@/modules/lib/utils"
import { type FieldValueChange } from "@/typings/formik"
import { type FieldInputProps } from "formik"

type StandardInputProps<T, K> = {
  label?: string
  description?: string
  name: string
  isRequiredInput?: boolean
  setFieldValue: FieldValueChange<K>
  getFieldProps: (nameOrOptions: T) => FieldInputProps<string | number | null>
  error?: React.JSX.Element
  className?: string
  containerClassName?: string
  isValueHidden?: boolean
  inputProps?: React.ComponentProps<"input"> & { "data-testid"?: string }
  children?: React.ReactNode
} & React.ComponentProps<"div">

export function StandardInput<T, K>({
  label,
  description,
  name,
  isRequiredInput = false,
  setFieldValue,
  getFieldProps,
  error,
  className,
  containerClassName,
  isValueHidden,
  inputProps,
  children,
  ...props
}: StandardInputProps<T, K>) {
  const value = getFieldProps(name as T).value ?? ""
  const textValue = isValueHidden ? "" : value

  return (
    <InputContainer
      label={label}
      description={description}
      name={name}
      isRequiredInput={isRequiredInput}
      error={error}
      className={className}
      {...props}
    >
      <div className={cn("inline-flex flex-col gap-2", containerClassName)}>
        <Input
          id={name}
          value={textValue}
          onChange={inputProps?.onChange ? inputProps.onChange : (v) => setFieldValue(name, v.target.value)}
          className={cn(inputProps?.className ?? "")}
          onBlur={getFieldProps(name as T)?.onBlur}
          onFocus={
            inputProps?.onFocus
              ? inputProps.onFocus
              : (e) => e.target.addEventListener("wheel", (e) => e.preventDefault(), { passive: false })
          }
          {...{ "aria-invalid": error ? true : false }}
          {...inputProps}
        />
        {children}
      </div>
    </InputContainer>
  )
}
